import React, { useState, useEffect } from 'react';
import useScrollReveal from '../../hooks/useScrollReveal';
import Reveal from '../common/Reveal';

export function SectionHeader({ title, accent, sub }) {
  return (
    <Reveal>
      <div style={{ textAlign: 'center', marginBottom: 48 }}>
        <h2 className="serif" style={{ fontSize: 'clamp(2rem,4vw,3rem)', fontWeight: 700, color: 'var(--dark)', marginBottom: 8 }}>
          {title} <span style={{ color: 'var(--saffron)' }}>{accent}</span>
        </h2>
        <div style={{ width: 60, height: 3, background: 'linear-gradient(90deg,var(--saffron),var(--gold))', margin: '12px auto 0', borderRadius: 2 }} />
        {sub && <p style={{ marginTop: 12, color: 'var(--brown)', fontSize: '1.05rem', fontWeight: 300 }}>{sub}</p>}
      </div>
    </Reveal>
  );
}

export function BtnPrimary({ children, onClick, style, href, ...props }) {
  const s = {
    display: 'inline-flex', alignItems: 'center', gap: 8, padding: '14px 30px', borderRadius: 12, border: 'none',
    cursor: 'pointer', fontFamily: "'Outfit',sans-serif", fontSize: 15, fontWeight: 600,
    background: 'linear-gradient(135deg,var(--saffron),var(--saffron-dark))', color: 'white',
    boxShadow: '0 4px 20px rgba(232,133,26,.3)', transition: 'transform .2s,box-shadow .2s',
    textDecoration: 'none', ...style,
  };
  if (href) return <a href={href} style={s} {...props}>{children}</a>;
  return <button onClick={onClick} style={s} {...props}>{children}</button>;
}

export function BtnOutline({ children, onClick, style, href, ...props }) {
  const s = {
    display: 'inline-flex', alignItems: 'center', gap: 8, padding: '14px 30px', borderRadius: 12,
    cursor: 'pointer', fontFamily: "'Outfit',sans-serif", fontSize: 15, fontWeight: 600,
    background: 'transparent', color: 'var(--saffron-dark)', border: '2px solid rgba(232,133,26,.4)',
    transition: 'all .25s', textDecoration: 'none', ...style,
  };
  if (href) return <a href={href} style={s} {...props}>{children}</a>;
  return <button onClick={onClick} style={s} {...props}>{children}</button>;
}

export function Tag({ children, type, style }) {
  const colors = {
    event: { background: 'rgba(220,38,38,.08)', color: '#DC2626' },
    Sufism: { background: 'rgba(232,133,26,.12)', color: 'var(--saffron-dark)' },
    Literature: { background: 'rgba(92,58,30,.1)', color: 'var(--brown)' },
    Art: { background: 'rgba(212,168,71,.15)', color: '#8B6914' },
    Culture: { background: 'rgba(34,139,34,.1)', color: '#228B22' },
  };
  const c = colors[type] || { background: 'var(--soft-orange)', color: 'var(--saffron-dark)' };
  return (
    <span style={{ display: 'inline-block', padding: '4px 12px', borderRadius: 20, fontSize: 12, fontWeight: 600, ...c, ...style }}>
      {children}
    </span>
  );
}

export function Pill({ children, active, onClick, style }) {
  return (
    <button
      onClick={onClick}
      style={{
        padding: '8px 20px', borderRadius: 24, border: '1.5px solid rgba(232,133,26,.2)',
        background: active ? 'linear-gradient(135deg,var(--saffron),var(--saffron-dark))' : 'white',
        color: active ? 'white' : 'var(--brown)', cursor: 'pointer', fontSize: 14, fontWeight: 500,
        fontFamily: "'Outfit',sans-serif", transition: 'all .25s', transform: active ? 'scale(1.05)' : 'scale(1)',
        ...style,
      }}
    >
      {children}
    </button>
  );
}

export function Card({ children, style, onClick, className = '' }) {
  return (
    <div
      className={className}
      onClick={onClick}
      style={{
        background: 'white', borderRadius: 16, overflow: 'hidden',
        border: '1px solid rgba(232,133,26,.08)', boxShadow: '0 2px 12px rgba(44,24,16,.03)',
        transition: 'transform .3s cubic-bezier(.4,0,.2,1),box-shadow .3s', cursor: onClick ? 'pointer' : 'default',
        ...style,
      }}
      onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-6px)'; e.currentTarget.style.boxShadow = '0 12px 40px rgba(232,133,26,.12)'; }}
      onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 2px 12px rgba(44,24,16,.03)'; }}
    >
      {children}
    </div>
  );
}

export function Counter({ end, suffix = '', label }) {
  const [count, setCount] = useState(0);
  const [ref, visible] = useScrollReveal();
  useEffect(() => {
    if (!visible) return;
    let cur = 0;
    const step = Math.ceil(end / 35);
    const iv = setInterval(() => { cur += step; if (cur >= end) { setCount(end); clearInterval(iv); } else setCount(cur); }, 35);
    return () => clearInterval(iv);
  }, [visible, end]);

  return (
    <div ref={ref} style={{ textAlign: 'center' }}>
      <div className="serif" style={{ fontSize: 'clamp(2rem,5vw,3rem)', fontWeight: 700, color: 'var(--saffron)' }}>{count}{suffix}</div>
      <div style={{ fontSize: 14, color: 'var(--brown)', marginTop: 4 }}>{label}</div>
    </div>
  );
}

export function FormGroup({ label, children }) {
  return (
    <div style={{ marginBottom: 18 }}>
      <label style={{ display: 'block', fontWeight: 500, marginBottom: 6, fontSize: 14, color: 'var(--brown)' }}>{label}</label>
      {children}
    </div>
  );
}

export const inputStyle = {
  width: '100%', padding: '12px 16px', border: '1.5px solid rgba(232,133,26,.15)', borderRadius: 10,
  fontFamily: "'Outfit',sans-serif", fontSize: 15, background: 'white', outline: 'none', transition: 'border-color .2s,box-shadow .2s',
};
